import React, { useState, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';
import ReactJoyride, { CallBackProps, TooltipRenderProps } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { isIOS, isSafari } from 'react-device-detect';
import { Header } from './components';
import Simulator from './components/simulator';
import DeviceHelper from '../../../core/helpers/DeviceHelper';
import Modal from '../Modal';
import PanelContent from '../PanelContent';
import { Label } from '..';
import AcademicDates from '../AcademicDates';
import Sidebar from './components/sidebar';
import Processing from '@/presentation/components/Processing';
import { Creators as logoutActions } from '../../../domain/useCases/login/signOutUseCase';
import { Creators as AcademicDatesCreator } from '../../../domain/useCases/schedule/getAcademicDatesUseCase';
import { Creators as selectAcademicDatesCreator } from '../../../domain/useCases/selectAcademicDateUseCase';
import { Creators as simulatorActions } from '../../../domain/useCases/simulatorUseCase/setSimulator';
import { Creators as profileActions } from '../../../domain/useCases/getProfile';
import { Creators as scheduleOnlyActions } from '../../../domain/useCases/schedule/getScheduleOnlyUseCase';
import { Creators as filterScheduleActions } from '../../../domain/useCases/schedule/filterByWeekUseCase';
import { Creators as getExamsActions } from '../../../domain/useCases/schedule/getExamsUseCase';
import { Creators as getScheduleByDayActions } from '../../../domain/useCases/schedule/getScheduleByDayUseCase';
import { Creators as coursePeriodActions } from '@/domain/useCases/courses/getPeriodCourses';
import { Creators as surveyValidationActions } from '@/domain/useCases/getSurveysValidation';
import { Creators as getEnrollmentCoursesActions } from '@/domain/useCases/getEnrollmentCourses';
import { Creators as getPeriodsActions } from '@/domain/useCases/payments/getPaymentPeriods/actions';
import {
  StepContainer,
  StepRow1,
  StepRowText
} from '@/presentation/modules/home/styles';
import useTutorial from '@/presentation/hooks/useTutorial';
import ModalConfirmation from '@/presentation/modules/procedures/components/Modal/Modal';
import TutorialView from '@/presentation/_components/tutorial/TutorialView';
import { useWindowSize } from '@/core/hooks/useWindowSize';
import { Creators as getAcademicPlanProgressActions } from '@/domain/useCases/getAcademicPlanProgress';
import { Creators as getPlanProgressDetailActions } from '@/domain/useCases/getPlanProgressDetail';
import { Creators as getPlanProgressResumeActions } from '@/domain/useCases/getPlanProgressResume';
import { Creators as getExamsRoleByStudentActions } from '@/domain/useCases/getExamsRoleByStudent';
import { Creators as studentTypeActions } from '@/domain/useCases/getStudentType';
import useExternalSettings from '@/presentation/hooks/useExternalSettings';
import { Creators as profileTurnActions } from '@/domain/useCases/getProfileTurn';
import {
  ContinueButton,
  ExitButton,
  TooltipBody,
  TooltipFooter
} from './style';
import { useNewStudents } from '@/core/hooks/useNewStudents';
import ModalSurveys from '@/presentation/_components/ModalSurveys/ModalSurveysController';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import ModalUpdateView from '@/presentation/_components/ModalUpdate/ModalUpdateController';

const Tooltip = ({
  isLastStep,
  step,
  primaryProps,
  skipProps,
  tooltipProps
}: TooltipRenderProps) => {
  const { width } = useWindowSize();
  return (
    <TooltipBody
      currentStep={step.target.toString()}
      {...tooltipProps}
      width={width}
    >
      {step.content}
      <TooltipFooter>
        <ContinueButton
          {...{
            ...primaryProps,
            title: 'Continuar'
          }}
          data-testid="cmp-button-continuar"
        >
          {isLastStep ? 'Finalizar' : 'Continuar'}
        </ContinueButton>
        {!isLastStep && (
          <ExitButton
            {...{
              ...skipProps,
              title: 'Salir'
            }}
            data-testid="cmp-button-salir"
          >
            Salir
          </ExitButton>
        )}
      </TooltipFooter>
    </TooltipBody>
  );
};

const stepsTutorial: any = {
  steps: [
    {
      target: '.step1',
      disableBeacon: true,
      isFixed: true,
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">1 </b>
          </StepRow1>
          <StepRowText>
            Revisa la información actual que necesitas para tu vida
            universitaria.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    },
    {
      target: '.step2',
      disableBeacon: true,
      isFixed: true,
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">2 </b>
          </StepRow1>
          <StepRowText>
            Revisa tus programaciones de todas tus clases durante el ciclo,
            además de tus reservas programadas.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    },
    {
      target: '.step3',
      disableBeacon: true,
      isFixed: true,
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">3 </b>
          </StepRow1>
          <StepRowText>
            Accede a los detalles de tus cursos, historial de notas, plan de
            estudios y otros detalles importantes.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    },

    {
      target: '.step4',
      disableBeacon: true,
      isFixed: true,
      placement: 'bottom-end',
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">4 </b>
          </StepRow1>
          <StepRowText>
            Revisa tus pagos pendientes y realizados, así como las opciones de
            pago disponibles.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    },
    {
      target: '.step5',
      disableBeacon: true,
      isFixed: true,
      placement: 'bottom-end',
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">5 </b>
          </StepRow1>
          <StepRowText>
            Descubre los servicios disponibles para ti: eventos, refuerzos
            académicos y recursos de biblioteca.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    },
    {
      target: '.step6',
      disableBeacon: true,
      isFixed: true,
      placement: 'bottom-end',
      content: (
        <StepContainer>
          <StepRow1>
            <div className="fractionText">/6</div>
            <b className="boulderText">6</b>
          </StepRow1>
          <StepRowText>
            Realiza una variedad de trámites e infórmate sobre los requisitos y
            plazos necesarios para cada uno.
          </StepRowText>
        </StepContainer>
      ),
      floaterProps: {
        disableAnimation: true
      },
      spotlightPadding: 0
    }
  ]
};

export const GlobalStylesSidebar = createGlobalStyle`
  #sidebarElement {
    overflow: initial !important;
  }
`;

const Layout = () => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const [someDevice, setSomeDevice] = useState(
    DeviceHelper.detectStateDevice()
  );
  const [openMenuMobile, setOpenMenuMobile] = useState(false);
  const [openAcademicsDates, setOpenAcademicsDates] = useState(false);
  const [storageChange, setStorageChange] = useState(0);
  const [runJoyRide, setRunJoyRide] = useState(false);
  const { viewedTutorial, setTutorial } = useTutorial();
  const { isValidToken, isLoadingSignOut } = useSelector(
    (state: any) => state.auth
  );
  const { isSimulator } = useSelector((state: any) => state.simulator);
  const { currentPeriod } = useExternalSettings();
  const newStudent = useNewStudents();

  const handleOpenMenuMobile = () => {
    setOpenMenuMobile(true);
    document.getElementsByTagName('body')[0].classList.add('no-scroll');
  };
  const handleCloseMenuMobile = () => {
    setOpenMenuMobile(false);
    document.getElementsByTagName('body')[0].classList.remove('no-scroll');
  };
  const handleInactiveSimulate = () => {
    GoogleAnalytics.writeLog('portal-simulation-endSimulation', 'simulation');
    dispatch(simulatorActions.inactiveSimulator());
    dispatch(profileActions.clearProfile());
    localStorage.removeItem('profileData');
    localStorage.removeItem('persist:profile');
    localStorage.removeItem('persist:profilePhoto');
    dispatch(scheduleOnlyActions.getScheduleOnlyClear());
    dispatch(filterScheduleActions.filterByWeekClear());
    dispatch(getExamsActions.getExamsClear());
    dispatch(getScheduleByDayActions.getScheduleByDayClear());
    dispatch(getAcademicPlanProgressActions.getAcademicPlanProgressClear());
    dispatch(getPlanProgressDetailActions.getPlanProgressDetailClear());
    dispatch(getPlanProgressResumeActions.getPlanProgressResumeClear());
    dispatch(profileTurnActions.getProfileTurnClear());
    dispatch(coursePeriodActions.getCoursePeriodClear());
    dispatch(getEnrollmentCoursesActions.getEnrollmentCoursesClear());
    dispatch(getExamsRoleByStudentActions.getExamsRoleByStudentClear());
    dispatch(getPeriodsActions.getPaymentPeriodsClear());
    localStorage.clear();
  };

  const onClickLogout = () => {
    dispatch(logoutActions.signOut());
  };

  const callSurveysValidation = () => {
    dispatch(surveyValidationActions.getSurveyValidationClear());
    dispatch(surveyValidationActions.getSurveyValidation());
  };

  useEffect(() => {
    callSurveysValidation();
    function handleResize() {
      setSomeDevice(DeviceHelper.detectStateDevice());
    }
    return window.addEventListener('resize', handleResize);
  }, []);

  const academicDates = useSelector((state: any) => state.academicdates?.data);
  const academicDateSelected = useSelector(
    (state: any) => state.selectAcademicDate?.selected
  );

  useEffect(() => {
    const ids = `id-${academicDateSelected}`;
    let targetElement;
    if (academicDateSelected !== 0) {
      targetElement = document.getElementById(ids);
    } else {
      targetElement = document.querySelector('#WrapScroll > div:first-child');
    }
    // WrapScroll
    if (isIOS || isSafari) {
      targetElement?.scrollIntoView(true);
    } else {
      targetElement?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [academicDateSelected]);

  useEffect(() => {
    const handleStorageChange = () => {
      setStorageChange((prev) => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const getStartStep = localStorage.getItem('_startStep');

    if (getStartStep) {
      handleOpenMenuMobile();
    }
  }, [storageChange]);

  useEffect(() => {
    if (runJoyRide) {
      setOpenMenuMobile(true);
    }
  }, [width]);

  useEffect(() => {
    if (currentPeriod) {
      dispatch(studentTypeActions.getStudentType(currentPeriod));
      dispatch(AcademicDatesCreator.getAcademicDates());
    }
  }, [currentPeriod]);

  const handlerCloseModalAcademicDate = () => {
    dispatch(selectAcademicDatesCreator.selectAcademicDateClear());
    setOpenAcademicsDates(false);
  };

  if (!isValidToken) {
    return <></>;
  }

  const onHiddeModal = () => {
    localStorage.setItem('__STEP_HIDDE', JSON.parse('null'));

    window.dispatchEvent(
      new CustomEvent('local-storage-changed', {
        detail: { event: '__STEP_HIDDE', value: 'null' }
      })
    );
  };

  const onSkip = () => {
    const navBar = document.getElementById('bottomMovie');
    localStorage.setItem('__STEP_HIDDE', JSON.parse('true'));
    setRunJoyRide(false);
    setTutorial(false);
    document.getElementsByTagName('body')[0].classList.remove('no-scroll');
    navBar?.click();

    onHiddeModal();
  };

  const handleJoyrideCallback = (joyridedata: CallBackProps) => {
    const { action } = joyridedata;
    if (action === 'skip' || action === 'reset') {
      onSkip();
    } else if (action === 'next') {
      setTutorial(true);
    }
  };

  const onStart = () => {
    const navBar = document.getElementById('barLayer');
    localStorage.setItem('__STEP_HIDDE', JSON.parse('true'));

    window.dispatchEvent(
      new CustomEvent('local-storage-changed', {
        detail: { event: '__STEP_HIDDE', value: 'true' }
      })
    );

    if (navBar && width <= 991) {
      navBar?.click();
    }

    setTimeout(() => {
      setRunJoyRide(true);
      setTutorial(true);
    }, 250);
  };

  return (
    <div data-testid="cmp-main-layout" className="cmpMainLayout">
      {runJoyRide && <GlobalStylesSidebar />}
      <ReactJoyride
        callback={handleJoyrideCallback}
        run={runJoyRide}
        disableScrolling
        steps={stepsTutorial.steps}
        tooltipComponent={Tooltip}
        styles={{
          options: {
            arrowColor: 'none',
            overlayColor: 'rgba(15,19,31,0.5)'
          }
        }}
      />
      <Sidebar
        isOpenMenuMobile={openMenuMobile}
        onClickLogout={onClickLogout}
        onCloseMenuMobile={handleCloseMenuMobile}
        onOpenMenuMobile={handleOpenMenuMobile}
      />
      <Header
        OnOpenMenuMobile={handleOpenMenuMobile}
        // handleSelectAcademicCalendar={() => setOpenAcademicsDates(true)}
        currentDevice={someDevice}
        // handleOpenModal={handleOpenModal}
        onClickLogout={onClickLogout}
      />
      <Processing isOpen={isLoadingSignOut} />
      <ModalConfirmation
        // show={showModalStorage}
        show={!viewedTutorial && !runJoyRide && !newStudent}
        handleClose={onSkip}
      >
        <TutorialView onSkip={onSkip} onStart={onStart} />
      </ModalConfirmation>
      {isSimulator && !openMenuMobile && (
        <Simulator handleInactiveSimulate={handleInactiveSimulate} />
      )}
      <Modal
        IsModalOpen={openAcademicsDates || academicDateSelected}
        handleCloseModal={handlerCloseModalAcademicDate}
        buttonClose
      >
        <PanelContent
          IsModalOpen={openAcademicsDates || academicDateSelected}
          heading={<Label text="Fechas académicas" />}
          content={
            <AcademicDates
              idSelected={academicDateSelected}
              scroll={false}
              datesForNextDays={
                typeof academicDates?.forComingNext === 'object'
                  ? academicDates?.forComingNext
                  : null
              }
              datesForToday={
                typeof academicDates?.forToday === 'object'
                  ? academicDates?.forToday
                  : null
              }
            />
          }
        />
      </Modal>
      <ModalSurveys />
      <ModalUpdateView />
    </div>
  );
};

export default Layout;
